import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/context/AuthContext";
import {
  CalendarCheck,
  CurrencyCircleDollar,
  Bell,
  EnvelopeSimple,
  CaretRight,
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const Kpi = ({ icon: Icon, label, value, sub, tone = "forest", testid }) => {
  const tones = {
    forest: "bg-forest-50 text-forest-600",
    terra: "bg-[#FBEAE3] text-terra-500",
    sand: "bg-sand-200 text-stone-700",
  };
  return (
    <Card className="border-stone-200 shadow-none rounded-xl p-6 hover-lift bg-white" data-testid={testid}>
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${tones[tone]}`}>
        <Icon size={22} weight="duotone" />
      </div>
      <div className="mt-4">
        <div className="text-[11px] uppercase tracking-[0.2em] text-stone-400">{label}</div>
        <div className="font-heading text-3xl font-semibold text-stone-900 mt-1">{value}</div>
        {sub && <div className="text-sm text-stone-500 mt-1">{sub}</div>}
      </div>
    </Card>
  );
};

export default function Overview() {
  const { user } = useAuth();
  const [kpis, setKpis] = useState(null);
  const [recent, setRecent] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.get("/dashboard/kpis"),
        api.get("/dashboard/recent-activity"),
      ]);
      setKpis(a.data);
      setRecent(b.data);
    } catch (e) {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const reseed = async () => {
    try {
      await api.post("/seed/demo");
      toast.success("Demo data reset");
      load();
    } catch { toast.error("Could not reseed"); }
  };

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="overview-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-stone-400">Dashboard</div>
          <h1 className="font-heading text-4xl font-semibold text-stone-900 mt-1">
            Good to see you, {user?.full_name?.split(" ")[0]}.
          </h1>
          <p className="text-stone-500 mt-1">Here's what's happening across your properties today.</p>
        </div>
        <Button variant="outline" onClick={reseed} className="border-stone-300" data-testid="reseed-demo-button">
          Reset demo data
        </Button>
      </div>

      {loading && <div className="text-stone-400">Loading…</div>}

      {kpis && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          <Kpi
            icon={CalendarCheck}
            label="Renewals Due (60d)"
            value={kpis.upcoming_renewals}
            sub="Review and draft offers"
            tone="forest"
            testid="kpi-renewals"
          />
          <Kpi
            icon={CurrencyCircleDollar}
            label="Past-Due Balance"
            value={`$${kpis.past_due_total.toLocaleString()}`}
            sub={`${kpis.past_due_count} tenants overdue`}
            tone="terra"
            testid="kpi-past-due"
          />
          <Kpi
            icon={Bell}
            label="Priority Emails"
            value={kpis.priority_emails}
            sub="Emergency & past-due"
            tone="terra"
            testid="kpi-priority"
          />
          <Kpi
            icon={EnvelopeSimple}
            label="Unread Emails"
            value={kpis.unread_emails}
            sub="Across all categories"
            tone="sand"
            testid="kpi-unread"
          />
        </div>
      )}

      {recent && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8">
          <Card className="lg:col-span-2 p-6 border-stone-200 shadow-none rounded-xl bg-white">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-heading text-xl font-medium">Upcoming Renewals</h2>
              <Link to="/app/renewals" className="text-sm text-forest-600 hover:underline flex items-center gap-1" data-testid="link-all-renewals">
                View all <CaretRight size={14} />
              </Link>
            </div>
            <div className="divide-y divide-stone-100">
              {recent.leases.map((l) => (
                <div key={l.id} className="py-3 flex items-center justify-between" data-testid={`overview-lease-${l.id}`}>
                  <div>
                    <div className="font-medium text-stone-900">{l.tenant_name}</div>
                    <div className="text-sm text-stone-500">{l.property_address}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-stone-900">Ends {new Date(l.end_date).toLocaleDateString()}</div>
                    <div className="text-xs text-stone-500">${l.current_rent} → ${l.proposed_rent}</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card className="p-6 border-stone-200 shadow-none rounded-xl bg-white">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-heading text-xl font-medium">Top Past-Due</h2>
              <Link to="/app/past-due" className="text-sm text-forest-600 hover:underline flex items-center gap-1" data-testid="link-all-past-due">
                View all <CaretRight size={14} />
              </Link>
            </div>
            <div className="divide-y divide-stone-100">
              {recent.past_due_tenants.map((t) => (
                <div key={t.id} className="py-3" data-testid={`overview-pd-${t.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="font-medium text-stone-900">{t.name}</div>
                    <div className="font-heading text-terra-500 font-semibold">${t.balance_due.toLocaleString()}</div>
                  </div>
                  <div className="text-xs text-stone-500 mt-0.5">
                    {t.days_overdue}d overdue · {t.property_address}
                  </div>
                </div>
              ))}
              {recent.past_due_tenants.length === 0 && (
                <div className="py-6 text-center text-sm text-stone-500">All clear 🌿</div>
              )}
            </div>
          </Card>

          <Card className="lg:col-span-3 p-6 border-stone-200 shadow-none rounded-xl bg-white">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-heading text-xl font-medium">Recent Inbox Activity</h2>
              <Link to="/app/triage" className="text-sm text-forest-600 hover:underline flex items-center gap-1" data-testid="link-all-triage">
                Open triage <CaretRight size={14} />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {recent.recent_emails.slice(0, 6).map((e) => (
                <div key={e.id} className="border border-stone-200 rounded-lg p-3 hover:bg-stone-50 transition-colors" data-testid={`overview-email-${e.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-stone-900 truncate">{e.from_name || e.from_addr}</div>
                    <CategoryChip category={e.category} />
                  </div>
                  <div className="text-sm text-stone-700 truncate mt-1">{e.subject}</div>
                  <div className="text-xs text-stone-500 truncate mt-0.5">{e.snippet}</div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

export const CategoryChip = ({ category }) => {
  const styles = {
    emergency: "bg-[#FBEAE3] text-terra-500 border-terra-300",
    past_due: "bg-[#FDF1EC] text-terra-500 border-terra-300",
    renewal: "bg-forest-50 text-forest-600 border-forest-200",
    vendor: "bg-[#EBF3EA] text-[#4B8158] border-sage-300",
    coworker: "bg-sand-200 text-stone-700 border-stone-300",
    general: "bg-sand-200 text-stone-500 border-stone-300",
  };
  const labels = {
    emergency: "Emergency",
    past_due: "Past-Due",
    renewal: "Renewal",
    vendor: "Vendor",
    coworker: "Coworker",
    general: "General",
  };
  return (
    <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-full border ${styles[category] || styles.general}`}>
      {labels[category] || "General"}
    </span>
  );
};
